"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { useStytchB2BClient, useStytchMember } from "@stytch/nextjs/b2b"
import { stashPostLoginRedirectFromQuery, consumePostLoginRedirect } from "@/lib/post-login-redirect"
import { validateBusinessEmail } from "@/lib/utils/emailValidation"

export default function Login() {
    const { member, isInitialized } = useStytchMember()
    const stytch = useStytchB2BClient()
    const router = useRouter()
    const [email, setEmail] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [isGoogleLoading, setIsGoogleLoading] = useState(false)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")

    // Middleware sets ?next= for deep links. Stash to localStorage so it survives
    // the magic-link round trip (which may open in a new tab).
    useEffect(() => {
        if (typeof window === "undefined") return
        const sp = new URLSearchParams(window.location.search)
        stashPostLoginRedirectFromQuery(sp.get("next"))
    }, [])

    // Already authenticated? Bounce to the deep-link target or the home page.
    useEffect(() => {
        if (isInitialized && member) {
            router.push(consumePostLoginRedirect() ?? "/")
        }
    }, [isInitialized, member, router])

    const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setError("")
        setSuccess("")
        setIsLoading(true)

        const validation = validateBusinessEmail(email)
        if (!validation.isValid) {
            setError(validation.error || "Invalid email")
            setIsLoading(false)
            return
        }

        try {
            // Discovery magic link — Stytch resolves which org(s) this email
            // belongs to. Works for invited cross-domain users too.
            await stytch.magicLinks.email.discovery.send({
                email_address: email,
                discovery_redirect_url: `${window.location.origin}/authenticate`,
            })
            setSuccess(`We've sent a magic link to ${email}. Check your email and click the link to sign in.`)
            setEmail("")
        } catch (err) {
            console.error("[Login] Error sending magic link:", err)
            setError("Failed to send magic link. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    const handleGoogleSignIn = async () => {
        setError("")
        setSuccess("")
        setIsGoogleLoading(true)
        try {
            stytch.oauth.google.discovery.start({
                discovery_redirect_url: `${window.location.origin}/authenticate`,
            })
        } catch (err) {
            console.error("[Login] Error initiating Google OAuth:", err)
            setIsGoogleLoading(false)
        }
    }

    if (!isInitialized) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="cardinal-secondary-font" style={{ color: "#853e30" }}>Loading...</div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="max-w-md w-full mx-auto px-6">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center">
                        <img src="/cardinal-bird-logo.png" alt="Cardinal" className="h-8 w-8" />
                    </div>
                    <h1 className="cardinal-primary-font text-3xl font-bold mb-2" style={{ color: "#853e30" }}>
                        Welcome to Cardinal
                    </h1>
                    <p className="cardinal-secondary-font text-gray-600">Sign in to your organization</p>
                </div>

                <div className="mb-6">
                    <button
                        onClick={handleGoogleSignIn}
                        disabled={isGoogleLoading || isLoading}
                        className="w-full cardinal-secondary-font flex justify-center items-center py-3 px-4 border border-gray-300 rounded-none font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#853e30] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        {isGoogleLoading ? "Redirecting to Google..." : (
                            <>
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Continue with Google
                            </>
                        )}
                    </button>
                </div>

                <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500 cardinal-secondary-font">Or continue with magic link</span>
                    </div>
                </div>

                <form className="space-y-6" onSubmit={handleEmailSubmit}>
                    <div>
                        <label htmlFor="email" className="cardinal-secondary-font block text-sm font-medium text-gray-700 mb-2">
                            Business Email
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-2 focus:ring-[#853e30] focus:border-transparent"
                            placeholder="you@company.com"
                            disabled={isLoading || isGoogleLoading}
                        />
                    </div>
                    {error && (
                        <div className="rounded-none bg-danger-bg p-4">
                            <div className="text-sm text-danger-hover cardinal-secondary-font">{error}</div>
                        </div>
                    )}
                    {success && (
                        <div className="rounded-none bg-green-50 p-4">
                            <div className="text-sm text-green-700 cardinal-secondary-font">{success}</div>
                        </div>
                    )}
                    <div>
                        <button
                            type="submit"
                            disabled={isLoading || isGoogleLoading}
                            className="w-full cardinal-secondary-font flex justify-center py-3 px-4 border border-transparent rounded-none font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#853e30] disabled:bg-gray-400 disabled:cursor-not-allowed"
                            style={{ backgroundColor: "#853e30" }}
                        >
                            {isLoading ? "Sending..." : "Send Magic Link"}
                        </button>
                    </div>
                </form>

                <div className="mt-6 text-center">
                    <p className="cardinal-secondary-font text-sm text-gray-600">Business emails required for magic links only</p>
                </div>
            </div>
        </div>
    )
}
